/* eslint-disable camelcase */

import { v4 as uuid } from 'uuid';
import type { GroupQuery } from '@atlassian/jira-business-common/src/common/types/grouping.tsx';
import { getJqlTerm } from '@atlassian/jira-business-common/src/common/utils/get-jql-term/index.tsx';
import { STORY_POINTS_ALIAS_FIELD_ID } from '@atlassian/jira-business-constants/src/index.tsx';
import { getFieldValue, safeGetValue } from '@atlassian/jira-business-fields/src/utils/index.tsx';
import { GROUP_ITEM_PREFIX } from '@atlassian/jira-business-list-handlers/src/common/constants.tsx';
import { isGroupItem } from '@atlassian/jira-business-list-handlers/src/common/utils/hierarchy/index.tsx';
import type { BaseGroupItemNode } from '@atlassian/jira-business-list-handlers/src/controllers/item-groups-cache/index.tsx';
import type { BaseIssueNode as BaseIssueNodeBase } from '@atlassian/jira-business-list-handlers/src/controllers/list-cache/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { getAriConfig } from '@atlassian/jira-platform-ari/src/index.tsx';
import { JiraIssueStatusAri } from '@atlassian/ari/jira/issue-status';
import { JiraPriorityAri } from '@atlassian/ari/jira/priority';
import {
	ASSIGNEE_TYPE,
	PRIORITY_TYPE,
	STATUS_TYPE,
	CATEGORY_TYPE,
	SPRINT_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
	NUMBER_CF_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import type {
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraPriorityField as PriorityField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraSingleSelectUserPickerField as UserField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraSingleSelectUserPickerField_user as UserValue,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraSingleSelectField as CategoryField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraSingleSelectField_fieldOption as CategoryOption,
} from '../get-list-view-issues/__generated_apollo__/JWMListViewItems';
import type { ListViewItemEdges_edges_node_fields } from '../get-list-view-issues/__generated_apollo__/ListViewItemEdges';
import type { ListViewIssue } from '../get-list-view-issues/types.tsx';
import type {
	JWMListViewItemGroupsWithPagination_groups_nodes,
	JWMListViewItemGroupsWithPagination_groups_nodes_field,
	JWMListViewItemGroupsWithPagination_groups_nodes_field_UserField as UserFieldGroup,
	JWMListViewItemGroupsWithPagination_groups_nodes_field_PriorityField as PriorityFieldGroup,
	JWMListViewItemGroupsWithPagination_groups_nodes_field_FloatCustomField as FloatCustomFieldGroup,
	JWMListViewItemGroupsWithPagination_groups_nodes_field_JWMCategoryCustomField_category as JWMCategoryCustomFieldCategory,
	JWMListViewItemGroupsWithPagination_groups_nodes_field_JWMCategoryCustomField as CategoryFieldGroup,
} from './__generated_apollo__/JWMListViewItemGroupsWithPagination';
import type { GroupByFieldId, GroupItemsQueryData } from './types.tsx';

type BaseIssueNode = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	readonly issueId: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	readonly fields: ReadonlyArray<any> | null | undefined;
};

export const createGroupIssueId = (nodeId: string) => `${GROUP_ITEM_PREFIX}_${nodeId}`;

export const getStatusId = (statusIdPath: string | null = '') => {
	if (statusIdPath == null) {
		return '';
	}
	const splitStatusIdPath = statusIdPath.split('/');
	return splitStatusIdPath[splitStatusIdPath.length - 1];
};

const getCategory = (
	field: JWMListViewItemGroupsWithPagination_groups_nodes_field | null | undefined,
) => {
	if (field && 'category' in field && field.category != null && field.category.option != null) {
		return {
			fieldKey: field.fieldKey,
			name: field.category.option.value,
			optionId: field.category.option.optionId,
			color: field.category.color ?? null,
		};
	}

	return {};
};

const getStoryPoints = (
	field: JWMListViewItemGroupsWithPagination_groups_nodes_field | null | undefined,
) => (field?.__typename === 'FloatCustomField' && 'floatValue' in field ? field.floatValue : null);

const getSprint = (
	field: JWMListViewItemGroupsWithPagination_groups_nodes_field | null | undefined,
) => {
	if (field && field.__typename === 'SprintCustomField' && field.values && field.values[0]) {
		return {
			fieldKey: field.fieldKey,
			title: field.title,
			id: field.values[0].id,
			name: field.values[0].name,
			state: field.values[0].state,
		};
	}
	return null;
};

export const transformResult = (
	nodes?: ReadonlyArray<JWMListViewItemGroupsWithPagination_groups_nodes | null | undefined>,
): ListViewIssue[] => {
	if (!nodes) {
		return [];
	}

	return nodes.map((node) => {
		const { id, field } = node || {};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const values: Record<string, any> = {};

		if (field && 'fieldKey' in field && field.fieldKey != null) {
			values[field.fieldKey] = field;
		}

		const totalCount = node?.totalCount;

		const groupItem: ListViewIssue = {
			id,
			issueId: createGroupIssueId(id || ''),
			key: '',
			typeId: '',
			issuetype: {},
			rank: '',
			status: values.status
				? {
						id: values.status.statusId,
						name: values.status.name,
						categoryId: values.status.statusCategoryId,
					}
				: {},
			...(values.assignee
				? values.assignee.userValue && {
						assignee: {
							accountId: values.assignee.userValue.accountId,
							displayName: values.assignee.userValue.displayName,
							avatarUrl: values.assignee.userValue.avatarUrl,
						},
					}
				: {}),
			labels: (values.labels && values.labels.labels) || [],
			priority: values.priority
				? {
						id: values.priority.id,
						name: values.priority.name,
						iconUrl: values.priority.iconUrl,
					}
				: {},
			category: getCategory(field),
			childSummary: {
				totalCount,
				hasChildren: (totalCount ?? 0) > 0,
			},
			storypoints: getStoryPoints(field),
			storypointestimate: getStoryPoints(field),
			sprint: getSprint(field),
		};

		return groupItem;
	});
};

// eslint-disable-next-line complexity
export const getGroupQuery = ({
	groupIssueId,
	groupByFieldId,
	groups,
}: {
	groupIssueId: string;
	groupByFieldId?: GroupByFieldId;
	groups?: ListViewIssue[];
}): GroupQuery | undefined => {
	if (!groups || !groupByFieldId) {
		return undefined;
	}

	const groupItem = groups.find((group) => group.issueId === groupIssueId);

	if (!groupItem) {
		return undefined;
	}

	if (groupByFieldId === ASSIGNEE_TYPE) {
		return {
			jqlTerm: ASSIGNEE_TYPE,
			value: groupItem.assignee?.accountId,
		};
	}

	if (groupByFieldId === PRIORITY_TYPE) {
		const priorityAri = groupItem?.priority?.id;
		let priorityId = priorityAri ? getAriConfig(priorityAri).resourceId : undefined;

		if (fg('migrate-deprecated-jira-platform-ari-fns')) {
			priorityId =
				priorityAri && JiraPriorityAri.check(priorityAri)
					? JiraPriorityAri.parse(priorityAri).priorityId
					: undefined;
		}

		return {
			jqlTerm: PRIORITY_TYPE,
			value: priorityId,
		};
	}

	if (groupByFieldId === STATUS_TYPE) {
		const statusAri = groupItem?.status?.id;
		let statusId = statusAri ? getAriConfig(statusAri).resourceId : undefined;

		if (fg('migrate-deprecated-jira-platform-ari-fns')) {
			statusId =
				statusAri && JiraIssueStatusAri.check(statusAri)
					? JiraIssueStatusAri.parse(statusAri).issuestatusId
					: undefined;
		}

		return {
			jqlTerm: STATUS_TYPE,
			value: statusId,
		};
	}

	if (groupByFieldId.startsWith('customfield_')) {
		const getValue = () => {
			if (groupItem.category) {
				const categoryName = groupItem.category?.name ?? undefined;
				if (categoryName) {
					// wrap with quotes and escape quotes
					return `"${categoryName.replace(/"/g, '\\"')}"`;
				}
			}
			if (typeof groupItem.storypoints === 'number') {
				return groupItem.storypoints;
			}
			if (typeof groupItem.storypointestimate === 'number') {
				return groupItem.storypointestimate;
			}
			if (groupItem.sprint) {
				return (groupItem.sprint && String(groupItem.sprint?.id)) || undefined;
			}

			return undefined;
		};

		return {
			jqlTerm: getJqlTerm(groupByFieldId),
			value: getValue(),
		};
	}

	return undefined;
};

// returns a group item where the "node" should be based on current grouping
export const findItemGroup = ({
	item,
	groups,
	groupByFieldId,
	isGroupingByCategory = false,
}: {
	item: BaseIssueNode | BaseIssueNodeBase;
	groups?: ListViewIssue[];
	groupByFieldId?: GroupByFieldId;
	isGroupingByCategory?: boolean;
}): ListViewIssue | undefined => {
	if (!groups || !groupByFieldId) {
		return undefined;
	}
	const field = getFieldValue(item.fields, groupByFieldId);
	// if item doesn't support category field but list is grouped by category, return the uncategorized group
	if (!field && isGroupingByCategory) {
		return groups.find((group) => !group.category?.name);
	}

	return groups.find((group) => {
		switch (field?.type) {
			case CATEGORY_TYPE:
				return (
					String(group?.category?.optionId) ===
					String(
						safeGetValue<CategoryOption | undefined>(
							item.fields,
							groupByFieldId,
							'fieldOption',
							undefined,
						)?.optionId,
					)
				);
			case ASSIGNEE_TYPE:
				return (
					groupByFieldId &&
					group.assignee?.accountId ===
						safeGetValue<UserValue | undefined>(item.fields, groupByFieldId, 'user', undefined)
							?.accountId
				);
			case STATUS_TYPE:
				return (
					groupByFieldId &&
					getStatusId(group.status?.id) ===
						safeGetValue(item.fields, groupByFieldId, 'status.statusId', undefined)
				);
			case PRIORITY_TYPE:
				return (
					groupByFieldId &&
					group.priority?.name ===
						safeGetValue(item.fields, groupByFieldId, 'priority.name', undefined)
				);
			case SPRINT_TYPE:
				return getSprintGroup(item, group);
			case STORY_POINT_ESTIMATE_CF_TYPE:
				return (
					groupByFieldId &&
					group.storypointestimate ===
						safeGetValue(item.fields, groupByFieldId, 'number', undefined)
				);
			case NUMBER_CF_TYPE:
				if (field.aliasFieldId === STORY_POINTS_ALIAS_FIELD_ID) {
					return (
						groupByFieldId &&
						group.storypoints === safeGetValue(item.fields, groupByFieldId, 'number', undefined)
					);
				}
				return false;
			default:
				return false;
		}
	});
};

const getSprintGroup = (item: BaseIssueNode | BaseIssueNodeBase, group: ListViewIssue) => {
	const sprintId = safeGetValue<string | undefined>(
		item.fields,
		'sprint',
		'selectedSprintsConnection.edges[0].node.sprintId',
		undefined,
	);

	if (!sprintId) {
		return group.sprint === null;
	}

	return group.sprint?.id === Number(sprintId);
};

// returns issueId of the group if it contains the "node"
export const getCurrentGroupIssueId = ({
	item,
	groupItemsQueryData,
}: {
	item: BaseIssueNode | BaseIssueNodeBase;
	groupItemsQueryData?: GroupItemsQueryData;
}): string | undefined => {
	if (!groupItemsQueryData) {
		return undefined;
	}
	const keys = Object.keys(groupItemsQueryData);

	// an issue can be in multiple groups due to hierarchy items appearing at all levels
	const matchingGroups = keys.filter((groupIssueId) =>
		// data could be undefined if is loading
		groupItemsQueryData[groupIssueId].data?.find((node) => node.issueId === item.issueId),
	);
	return matchingGroups.find((groupName) => isGroupItem(groupName)) ?? matchingGroups[0];
};

// TODO: JFP-2824 | Suppressed to enable upgrade to ESLint v9 - please fix this if you can!

const convertFieldToGroupItemField = (
	groupByFieldId: GroupByFieldId,
	field: ListViewItemEdges_edges_node_fields,
): JWMListViewItemGroupsWithPagination_groups_nodes_field | ListViewItemEdges_edges_node_fields => {
	if (groupByFieldId === ASSIGNEE_TYPE && field) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const userField = field as UserField;
		const convertedField: UserFieldGroup = {
			fieldKey: groupByFieldId,
			userValue: {
				accountId: userField.user?.accountId ?? null,
				displayName: userField.user?.name ?? null,
				avatarUrl: userField.user?.picture,
				__typename: 'UserValue',
			},
			__typename: 'UserField',
		};
		return convertedField;
	}

	if (groupByFieldId === PRIORITY_TYPE && field) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const priorityField = field as PriorityField;
		const convertedField: PriorityFieldGroup = {
			fieldKey: groupByFieldId,
			name: priorityField.priority?.name ?? null,
			id: priorityField.priority?.id ?? null,
			iconUrl: priorityField.priority?.iconUrl,
			__typename: 'PriorityField',
		};
		return convertedField;
	}

	if (groupByFieldId?.startsWith('customfield_') && field) {
		if (field.__typename === 'JiraSingleSelectField' && field.type === CATEGORY_TYPE) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const categoryField = field as CategoryField;
			if (categoryField.fieldOption) {
				const convertedField: CategoryFieldGroup = {
					category: {
						color:
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							(categoryField.fieldOption?.color
								?.colorKey as JWMCategoryCustomFieldCategory['color']) ?? null,
						id: uuid(),
						option: {
							id: uuid(),
							isDisabled: false,
							optionId: categoryField.fieldOption.optionId,
							value: categoryField.fieldOption.value,
							__typename: 'JiraOption',
						},
						__typename: 'JiraWorkManagementCategoryOption',
					},
					selectedValue: {
						optionId: categoryField.fieldOption.optionId,
						value: categoryField.fieldOption.value ?? '',
						__typename: 'Option',
					},
					fieldKey: field.fieldId,
					__typename: 'JWMCategoryCustomField',
				};
				return convertedField;
			}
		}
		if (field.__typename === 'JiraNumberField') {
			const convertedField: FloatCustomFieldGroup = {
				fieldKey: field.fieldId,
				floatValue: field.number,
				__typename: 'FloatCustomField',
			};
			return convertedField;
		}
	}

	return field;
};

export const createItemGroup = ({
	item,
	groupByFieldId,
}: {
	item: BaseIssueNode | BaseIssueNodeBase;
	groupByFieldId?: GroupByFieldId;
}): BaseGroupItemNode | undefined => {
	if (!item || !groupByFieldId) {
		return undefined;
	}

	let field = getFieldValue(item.fields, groupByFieldId);

	if (!field) {
		return;
	}

	if (field && groupByFieldId) {
		// TODO: Remove this part once JER-321 is done.
		field = convertFieldToGroupItemField(
			groupByFieldId,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			field as ListViewItemEdges_edges_node_fields,
		);
	}

	return {
		id: uuid(),
		field,
		totalCount: 1,
		__typename: 'JWMItemGroup',
	};
};
